import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import WorkGrid from "@/components/base/WorkGrid";
import EmptyState from "@/components/base/EmptyState";
import {
  defaultFilter,
  filterWorks,
  sortWorks,
  getGenres,
  getRegions,
  getYears,
  getWorksByCategory,
  SORT_OPTIONS,
  EPISODE_OPTIONS,
  type FilterState,
} from "@/utils/film";

const PAGE_SIZE = 8;

interface CatalogViewProps {
  category: "movie" | "tv";
  title: string;
  description: string;
  breadcrumb: string;
}

export default function CatalogView({
  category,
  title,
  description,
  breadcrumb,
}: CatalogViewProps) {
  const works = useMemo(() => getWorksByCategory(category), [category]);
  const genres = useMemo(() => getGenres(works), [works]);
  const regions = useMemo(() => getRegions(works), [works]);
  const years = useMemo(() => getYears(works), [works]);

  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [showFilters, setShowFilters] = useState(false);

  const result = useMemo(() => {
    return sortWorks(filterWorks(works, filter), filter.sort);
  }, [works, filter]);

  const visible = result.slice(0, visibleCount);
  const hasMore = visibleCount < result.length;

  const update = (patch: Partial<FilterState>) => {
    setFilter((prev) => ({ ...prev, ...patch }));
    setVisibleCount(PAGE_SIZE);
  };

  const reset = () => {
    setFilter(defaultFilter);
    setVisibleCount(PAGE_SIZE);
  };

  const chipClass = (active: boolean) =>
    `whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors pointer-events-auto ${
      active
        ? "bg-accent-500 text-background-50"
        : "bg-background-100 text-foreground-700 hover:bg-background-200"
    }`;

  const selectClass =
    "h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-accent-400 focus:ring-2 focus:ring-accent-100";

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-accent-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{breadcrumb}</span>
      </nav>

      <header className="mt-5">
        <h1 className="text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
          {title}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{description}</p>
      </header>

      <div className="mt-7 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
          <div className="flex flex-1 items-center gap-2 overflow-x-auto no-scrollbar">
            <button type="button" onClick={() => update({ genre: "all" })} className={chipClass(filter.genre === "all")}>
              全部类型
            </button>
            {genres.map((genre) => (
              <button
                key={genre}
                type="button"
                onClick={() => update({ genre })}
                className={chipClass(filter.genre === genre)}
              >
                {genre}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-medium text-background-50 md:hidden"
          >
            <i className="ri-filter-3-line"></i>
            筛选
          </button>
        </div>

        <div
          className={`${showFilters ? "grid" : "hidden"} mt-4 grid-cols-2 gap-3 border-t border-background-200 pt-4 md:grid md:grid-cols-4`}
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-500">地区</span>
            <select
              value={filter.region}
              onChange={(event) => update({ region: event.target.value })}
              className={selectClass}
            >
              <option value="all">全部地区</option>
              {regions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-500">年份</span>
            <select
              value={filter.year}
              onChange={(event) => update({ year: event.target.value })}
              className={selectClass}
            >
              <option value="all">全部年份</option>
              {years.map((year) => (
                <option key={year} value={String(year)}>
                  {year} 年
                </option>
              ))}
            </select>
          </label>

          {category === "tv" ? (
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-500">剧集数</span>
              <select
                value={filter.episodes}
                onChange={(event) => update({ episodes: event.target.value })}
                className={selectClass}
              >
                {EPISODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-500">排序</span>
            <select
              value={filter.sort}
              onChange={(event) => update({ sort: event.target.value })}
              className={selectClass}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
        <span>
          共找到 <strong className="text-accent-600">{result.length}</strong> 部作品
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-sort-desc"></i>
          {SORT_OPTIONS.find((option) => option.value === filter.sort)?.label}
        </span>
      </div>

      <div className="mt-5">
        {result.length === 0 ? (
          <EmptyState
            icon="ri-emotion-sad-line"
            title="未找到符合条件的作品"
            description="当前筛选条件下没有匹配的影视内容，试试调整或重置筛选条件。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        ) : (
          <>
            <WorkGrid works={visible} />
            {hasMore ? (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className="ri-add-line"></i>
                  加载更多
                </button>
              </div>
            ) : (
              <p className="mt-8 text-center text-sm text-foreground-400">已经到底啦</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}